import { useMemo, useState } from 'react'

import { useInputGaTracking } from 'aem-libs-react-ts/src/hooks/useInputGaTracking'
import { conditionalInvoke } from 'aem-libs-react-ts/src/utils/conditionalInvoke'
import { debounce } from 'aem-libs-ts/src/utils/common/debounce'
import { isDefined } from 'aem-libs-ts/src/utils/common/isDefined'
import { pipe } from 'aem-libs-ts/src/utils/common/pipe'
import { clampFp } from 'aem-libs-ts/src/utils/number/clamp'

import type { PlusMinusNumberFieldProps } from './PlusMinusNumberField'


const debounceDelay = 50

export const usePlusMinusNumberField = (props: PlusMinusNumberFieldProps) => {
  const [delayedValue, setDelayedValue] = useState<number | null>(null)

  const { trackInputChange } = useInputGaTracking({
    enableGaTracking: true,
    description: props.trackingDescription ?? props.label,
    id: props.id
  })

  const debouncedOnChangeComplete = useMemo(() => {
    if (!props.onChangeComplete) {
      return
    }

    const finishChange = (value: number) => {
      setDelayedValue(null)
      props.onChangeComplete?.(value)
    }

    return debounce(finishChange, debounceDelay)
  }, [props.onChangeComplete])

  const processClickValueChange = (step: number | null, onClickFcn: (() => void) | undefined) => {
    if (props.disabled) {
      return
    }
    conditionalInvoke(onClickFcn)
    if (!isDefined(step)) {
      return
    }
    const valueLimiter = clampFp(props.min, props.max)
    const newValue = pipe(() => delayedValue ?? props.value)
      .map((val) => val + step)
      .map(valueLimiter)
      .get()

    if (debouncedOnChangeComplete) {
      setDelayedValue(newValue as number)
      debouncedOnChangeComplete(newValue as number)
    }
    trackInputChange(newValue as number, 'button')
  }

  const handlePlusClick = () => processClickValueChange(props.step ? props.step : null, props.onPlus)

  const handleMinusClick = () => processClickValueChange(props.step ? -props.step : null, props.onMinus)

  const handleNumberFieldChange = (value: number) => {
    if (isDefined(value) && debouncedOnChangeComplete) {
      debouncedOnChangeComplete(value)
    }
  }

  return {
    delayedValue,
    handleNumberFieldChange,
    handlePlusClick,
    handleMinusClick
  }
}
